import { Card } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";

import DataTable from "components/organisms/DataTable/DataTable";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import ColumnFilter from "components/organisms/DataTable/components/ColumnFilter";
import ListViewTour from "components/tours/listView/listViewTour";
import ManageAccessDialog from "components/molecules/TopNav/common/ManageAccess/ManageAccessDialog";
import Filter from "components/organisms/Filter/Filter";
import useCommonShipmentListing from "pages/common/Shipments/hooks/useCommonShipmentListing";
import { getDateParams } from "helpers/commonHelpers";
import useShipmentFilters from "hooks/filters/useShipmentFilters";
import getProviderShipmentColumns from "./ShipmentColumns";

const PROVIDER_SHIPMENT_LIST_DEFAULT_COLUMN_ORDER = ["shipment", "payment_location", "freight_mode", "route", "status"];

function Shipments() {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const status: string = searchParams.get("display_status") || "";
  const { config: filterConfig } = useShipmentFilters({ status, type: "provider" });
  const {
    data,
    totalArchiveData,
    instance,
    archiveLoading,
    isLoading,
    showGuestAccessDialog,
    selectedShipmentId,
    handleStatusChange,
    handleRowClick,
    handleCloseManageAccessDialog,
    DISPLAY_STATUS,
    onColumnOrderChange,
  } = useCommonShipmentListing({
    getShipmentColumns,
    columnOrder: PROVIDER_SHIPMENT_LIST_DEFAULT_COLUMN_ORDER,
    archiveFilters: (tableInstance) => ({
      ...(tableInstance.state.globalFilter.provider_team?.length && {
        team_name: tableInstance.state.globalFilter.provider_team.join(","),
      }),
      ...(tableInstance.state.globalFilter.pickup_date?.type &&
        getDateParams(tableInstance.state.globalFilter.pickup_date, "pickup_date")),
      ...(tableInstance.state.globalFilter.delivery_date?.type &&
        getDateParams(tableInstance.state.globalFilter.delivery_date, "stop1_date")),
      ...(tableInstance.state.globalFilter.shipper?.length && {
        shipper: tableInstance.state.globalFilter.shipper.join(","),
      }),
    }),
  });

  function getShipmentColumns(intl: IntlShape, handleOpenGuestAccessDialog: Function) {
    return getProviderShipmentColumns(intl, handleOpenGuestAccessDialog);
  }

  return (
    <>
      <ListViewTour />
      <ManageAccessDialog
        shipmentId={selectedShipmentId}
        isOpen={showGuestAccessDialog}
        close={handleCloseManageAccessDialog}
      />
      <Card body className="shadow-none shipment-container">
        <div className="d-flex align-items-center mb-4">
          <h3 className="mr-3">
            <FormattedMessage id="general.shipments" defaultMessage="Shipments" />
          </h3>
          <GlobalTableSearch
            currentFilters={instance.state.globalFilter}
            setFilters={instance.setGlobalFilter}
            eventContext="Shipment List"
            filterKey="query"
          />
        </div>
        <DataTable
          autoPagination
          tableInstance={instance}
          totalData={status === DISPLAY_STATUS.ARCHIVED ? totalArchiveData : data[!status ? "all" : status].length}
          isLoading={status === DISPLAY_STATUS.ARCHIVED ? archiveLoading : isLoading}
          onRowClick={handleRowClick}
          onColumnOrderChange={onColumnOrderChange}
          headerLeft={
            <PillTabs
              id="shipment-statuses"
              activeKey={status}
              onSelect={handleStatusChange}
              tabItems={[
                {
                  eventKey: DISPLAY_STATUS.ALL,
                  title: `${intl.formatMessage({
                    id: "tabs.all",
                    defaultMessage: "All",
                  })} (${data.all.length})`,
                },
                {
                  eventKey: DISPLAY_STATUS.BOOKED,
                  title: `${intl.formatMessage({
                    id: "tabs.booked",
                    defaultMessage: "Booked",
                  })} (${data[DISPLAY_STATUS.BOOKED].length})`,
                },
                {
                  eventKey: DISPLAY_STATUS.SHIPPING,
                  title: `${intl.formatMessage({
                    id: "tabs.inTransit",
                    defaultMessage: "In Transit",
                  })} (${data[DISPLAY_STATUS.SHIPPING].length})`,
                },
                {
                  eventKey: DISPLAY_STATUS.DELAYED,
                  title: `${intl.formatMessage({
                    id: "tabs.delayed",
                    defaultMessage: "Delayed",
                  })} (${data[DISPLAY_STATUS.DELAYED].length})`,
                },
                {
                  eventKey: DISPLAY_STATUS.DELIVERED,
                  title: `${intl.formatMessage({
                    id: "tabs.delivered",
                    defaultMessage: "Delivered",
                  })} (${data[DISPLAY_STATUS.DELIVERED].length})`,
                },
                {
                  eventKey: DISPLAY_STATUS.INCOMPLETE,
                  title: `${intl.formatMessage({
                    id: "tabs.incomplete",
                    defaultMessage: "Incomplete",
                  })} (${data[DISPLAY_STATUS.INCOMPLETE].length})`,
                },
                {
                  eventKey: DISPLAY_STATUS.ARCHIVED,
                  title: intl.formatMessage({
                    id: "tabs.archived",
                    defaultMessage: "Archived",
                  }),
                },
              ]}
            />
          }
          headerRight={
            <div className="d-flex align-items-center">
              <Filter
                presetType="shipment"
                eventContext="Shipment List"
                currentFilters={instance.state.globalFilter}
                setFilters={instance.setGlobalFilter}
                config={filterConfig}
              />
              <div className="ml-3">
                <ColumnFilter columns={instance.allColumns} eventContext="Shipment List" listViewName="shipments" />
              </div>
            </div>
          }
          withoutBody
        />
      </Card>
    </>
  );
}

export default Shipments;
